import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { layoutForPath } from '../utils/layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Wrench, RefreshCw, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DateField from '../components/DateField';
import DataImportDialog from '../components/import/DataImportDialog';
import '../components/equipment/equipment-list.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  description: '',
  shareable: false,
  cost: '',
  cost_type: 'per_hour',
  status: 'available',
  billable: true,
  runtime_hours: '0',
  make: '',
  model: '',
  serial_number: '',
  purchase_date: '',
};

function Equipment() {
  const [equipment, setEquipment] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [maintenanceMap, setMaintenanceMap] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const { user } = useAuth();
  const inAdminDivision = useLocation().pathname.startsWith('/admin/');
  const equipmentBase = inAdminDivision ? '/admin/equipment' : '/equipment';
  const Layout = layoutForPath(useLocation().pathname);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API}/equipment`);
      setEquipment(response.data);
      const summaries = await Promise.all(
        response.data.map((eq) =>
          axios
            .get(`${API}/equipment/${eq.id}/maintenance-summary`)
            .then((r) => [eq.id, r.data])
            .catch(() => [eq.id, null])
        )
      );
      const map = {};
      summaries.forEach(([id, data]) => {
        if (data) map[id] = data;
      });
      setMaintenanceMap(map);
    } catch (error) {
      toast.error('Failed to load equipment');
    }
  };

  const openAddDialog = () => {
    setEditingEquipment(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEditDialog = (eq) => {
    setEditingEquipment(eq);
    setFormData({
      name: eq.name,
      description: eq.description || '',
      shareable: !!eq.shareable,
      cost: eq.cost?.toString() || '',
      cost_type: eq.cost_type || 'per_hour',
      status: eq.status || 'available',
      billable: eq.billable !== false,
      runtime_hours: (eq.runtime_hours ?? 0).toString(),
      make: eq.make || '',
      model: eq.model || '',
      serial_number: eq.serial_number || '',
      purchase_date: eq.purchase_date || '',
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        shareable: formData.shareable,
        ...(editingEquipment ? { revision: editingEquipment.revision } : {}),
        cost: parseFloat(formData.cost),
        cost_type: formData.cost_type,
        status: formData.status,
        billable: !!formData.billable,
        runtime_hours: parseFloat(formData.runtime_hours || '0') || 0,
        make: formData.make.trim() || null,
        model: formData.model.trim() || null,
        serial_number: formData.serial_number.trim() || null,
        purchase_date: formData.purchase_date || null,
        maintenance_schedules: editingEquipment?.maintenance_schedules || [],
      };

      if (editingEquipment) {
        await axios.patch(`${API}/equipment/${editingEquipment.id}`, payload);
        toast.success('Equipment updated');
      } else {
        await axios.post(`${API}/equipment`, payload);
        toast.success('Equipment added');
      }
      setShowDialog(false);
      setEditingEquipment(null);
      setFormData(emptyForm);
      fetchEquipment();
    } catch (error) {
      toast.error('Failed to save equipment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this equipment?')) return;
    try {
      await axios.delete(`${API}/equipment/${id}`);
      toast.success('Deleted');
      fetchEquipment();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const canManage = user?.role === 'management' || (user?.effective_permissions || []).includes('equipment.edit');

  const recalcRuntime = async () => {
    if (!window.confirm('Recalculate runtime hours for ALL equipment from historical job assignments? This will overwrite the current runtime values.')) return;
    try {
      const res = await axios.post(`${API}/equipment/recalculate-runtime`);
      const { updated_count, total_equipment } = res.data;
      toast.success(`Updated ${updated_count} of ${total_equipment} equipment`);
      fetchEquipment();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recalculation failed');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="equipment-page">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Equipment</h3>
            <p className="text-slate-600">Manage shop equipment</p>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={recalcRuntime}
                variant="outline"
                data-testid="recalc-runtime-button"
                title="Rebuild the Runtime column from all historical job equipment assignments"
              >
                <RefreshCw size={16} className="mr-2" />
                Recalculate Runtime
              </Button>
              {user?.role === 'management' && (
                <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-equipment-button">
                  <Upload size={16} className="mr-2" /> Import CSV
                </Button>
              )}
              <Button onClick={openAddDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-equipment-button">
                <Plus size={20} className="mr-2" />
                Add Equipment
              </Button>
            </div>
          )}
          <DataImportDialog kind="equipment" open={importOpen} onOpenChange={setImportOpen} onImported={fetchEquipment} />
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingEquipment ? 'Edit Equipment' : 'Add New Equipment'}</DialogTitle>
              <DialogDescription>Fill in cost, status, runtime hours, and whether this item is billable to jobs.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[75dvh] overflow-y-auto px-1" data-testid="equipment-form">
              <div className="space-y-2">
                <Label>Equipment Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="equipment-name-input"
                />
              </div>
              <div className="space-y-2"><Label htmlFor="shop-equipment-description">Description</Label><Textarea id="shop-equipment-description" data-testid="equipment-description-input" value={formData.description} maxLength={2000} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
              <div className="flex items-center justify-between gap-3"><Label htmlFor="shop-equipment-shareable">Shareable with field</Label><Switch id="shop-equipment-shareable" data-testid="equipment-shareable-switch" checked={formData.shareable} onCheckedChange={(shareable) => setFormData({ ...formData, shareable })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Make</Label>
                  <Input
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    placeholder="e.g. Mazak"
                    data-testid="equipment-make-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="e.g. QT-250"
                    data-testid="equipment-model-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Serial Number</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="e.g. QT-12345"
                    data-testid="equipment-serial-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date Purchased</Label>
                  <DateField
                    value={formData.purchase_date}
                    onChange={(v) => setFormData({ ...formData, purchase_date: v })}
                    data-testid="equipment-purchase-date-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Cost Type</Label>
                  <Select value={formData.cost_type} onValueChange={(value) => setFormData({ ...formData, cost_type: value })}>
                    <SelectTrigger data-testid="cost-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_hour">Per Hour</SelectItem>
                      <SelectItem value="per_use">Per Use</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cost ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    required
                    data-testid="equipment-cost-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="in_use">In Use</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Runtime Hours</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.runtime_hours}
                    onChange={(e) => setFormData({ ...formData, runtime_hours: e.target.value })}
                    data-testid="equipment-runtime-hours-input"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">Billable to jobs</div>
                  <div className="text-xs text-slate-500">Non-billable items are hidden from the job assignment dropdown.</div>
                </div>
                <Switch
                  checked={!!formData.billable}
                  onCheckedChange={(v) => setFormData({ ...formData, billable: !!v })}
                  data-testid="equipment-billable-switch"
                />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-equipment-button">
                {editingEquipment ? 'Update Equipment' : 'Add Equipment'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm">
          <table className="w-full shop-equipment-list" data-testid="equipment-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Equipment</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Cost Type</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Cost</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Runtime</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Uses</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Maint. Hrs</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {equipment.map((eq) => (
                <tr key={eq.id} className="hover:bg-slate-50" data-testid={`equipment-row-${eq.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => navigate(`${equipmentBase}/${eq.id}`)}
                        className="text-slate-900 hover:text-blue-600 hover:underline text-left"
                        data-testid={`equipment-name-link-${eq.id}`}
                      >
                        {eq.name}
                      </button>
                      {eq.shareable && <span className="text-xs text-sky-700" data-testid={`equipment-shared-${eq.id}`}>Shared with field</span>}
                      {eq.billable === false && (
                        <span
                          className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border border-slate-300 bg-slate-100 text-slate-700"
                          data-testid={`non-billable-pill-${eq.id}`}
                        >
                          Non-billable
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-label="Rate type" data-testid={`equipment-rate-type-${eq.id}`} className="px-6 py-4 text-sm text-slate-600 capitalize">{eq.cost_type?.replace('_', ' ') || 'Per Hour'}</td>
                  <td data-label="Rate" data-testid={`equipment-rate-${eq.id}`} className="px-6 py-4 text-sm font-mono text-slate-900">${eq.cost?.toFixed(2)}</td>
                  <td data-label="Status" data-testid={`equipment-status-${eq.id}`} className="px-6 py-4">
                    <span className={`inline-flex px-3 py-1 rounded-md text-xs font-semibold border ${
                      eq.status === 'available' ? 'bg-green-100 text-green-700 border-green-200' :
                      eq.status === 'in_use' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      'bg-amber-100 text-amber-700 border-amber-200'
                    }`}>
                      {eq.status?.replace('_', ' ') || 'available'}
                    </span>
                  </td>
                  <td data-label="Runtime hours" className="px-6 py-4 text-sm font-mono text-slate-700" data-testid={`runtime-hours-${eq.id}`}>
                    {(eq.runtime_hours || 0).toFixed(1)}
                  </td>
                  <td data-label="Uses" className="px-6 py-4 text-sm font-mono text-slate-700" data-testid={`use-count-${eq.id}`}>
                    {eq.use_count || 0}
                  </td>
                  <td data-label="Maintenance hours" className="px-6 py-4 text-sm font-mono text-slate-700" data-testid={`maintenance-hours-${eq.id}`}>
                    {maintenanceMap[eq.id]?.maintenance_hours > 0 ? (
                      <span>
                        {maintenanceMap[eq.id].maintenance_hours}
                        {maintenanceMap[eq.id].overtime_hours > 0 && (
                          <span className="ml-1 text-xs text-amber-700 font-semibold">
                            +{maintenanceMap[eq.id].overtime_hours} OT
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`${equipmentBase}/${eq.id}`)}
                        data-testid={`view-equipment-${eq.id}`}
                        title="View maintenance"
                      >
                        <Wrench size={16} />
                      </Button>
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(eq)}
                            data-testid={`edit-equipment-${eq.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(eq.id)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-equipment-${eq.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {equipment.length === 0 && (
            <div className="p-12 text-center text-slate-600">
              No equipment found
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Equipment;
